import { useCallback, useEffect, useState, useMemo } from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import CustomerPreviewSection, {
    CustomerSelectionValues,
} from '../../CustomerPreviewSection'
import toast from 'react-hot-toast'
import {
    CreateSurveyRecordPayload,
    RecordResponse,
} from '@/modules/Survey/types'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    createSurveyRecordsBulk,
    getCurrentQuarterSurveyState,
} from '@/modules/Survey/services'
import SubHeaders from '@/components/Headers/SubHeaders'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import StepIndicator from './components/StepIndicator'
import { User, FileText, Loader2 } from 'lucide-react'
import SurveyResponseTable from './components/SurveyResponseTable'
import { useSurveyCreateTour } from './use-survey-create-tour'
import { useTourHandler } from '@/contexts/tour-handler'
import { SURVEY_SCORE_MIN, SURVEY_SCORE_MAX } from '@/modules/Survey/constants'

const SESSION_STORAGE_KEY = 'SURVEY_RECORD_CREATE_CUSTOMER_SELECTION'

const SurveyRecordCreate = () => {
    const { t } = useTranslation('surveys')
    const navigate = useNavigate()
    const [customerSelection, setCustomerSelection] = useState<
        CustomerSelectionValues | undefined
    >(() => {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEY)
        return stored ? JSON.parse(stored) : undefined
    })
    const [isStep2Confirmed, setIsStep2Confirmed] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)

    const [responses, setResponses] = useState<CreateSurveyRecordPayload[]>([])
    const [isTourMode, setIsTourMode] = useState(false)

    // Tour integration with demo mode handlers
    const handleSetDemoData = useCallback((customers: any[]) => {
        setIsTourMode(true)
        setCustomerSelection({
            mode: 'manual',
            selectedIds: customers.map((c) => c.customer_id),
            selectedCustomers: customers,
        })
    }, [])

    const handleMoveToStep2 = useCallback(() => {
        // Don't check isTourMode here as it might not be set yet
        // The function is only called from tour, so we can assume it's tour mode
        setIsStep2Confirmed(true)
        setCurrentStep(2)
    }, [])

    const handleClearDemoData = useCallback(() => {
        setIsTourMode(false)
        setCustomerSelection(undefined)
        setIsStep2Confirmed(false)
        setCurrentStep(1)
        setResponses([])
    }, [])

    const tourOptions = useMemo(
        () => ({
            onSetDemoData: handleSetDemoData,
            onMoveToStep2: handleMoveToStep2,
            onClearDemoData: handleClearDemoData,
            getCurrentStep: () => currentStep,
        }),
        [handleSetDemoData, handleMoveToStep2, handleClearDemoData, currentStep]
    )

    const { startTour } = useSurveyCreateTour(tourOptions)
    const { registerHandler, unregisterHandler } = useTourHandler()

    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    // Must be fetched before customer preview is allowed
    const { data: quarterSurveyState, renderState: renderQuarterIdsState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SURVEYS, 'customer-ids-current-quarter'],
            queryFn: () => getCurrentQuarterSurveyState(),
        })
    const quarterCustomerIds = quarterSurveyState?.customer_ids

    // Answers already on file this quarter, keyed by customer. Submitting a
    // customer who is in here updates their record instead of creating a second
    // one, and the backend update is a full replace -- so step 2 must start from
    // these values or re-submitting would wipe the notes the first surveyor left.
    const existingResponsesByCustomer = useMemo(() => {
        const map = new Map<number, RecordResponse>()
        for (const record of quarterSurveyState?.records ?? []) {
            const {
                survey_record_id: _id,
                customer_id: _cid,
                ...answers
            } = record
            map.set(record.customer_id, answers)
        }
        return map
    }, [quarterSurveyState])

    const { mutateAsync: createBulkMutation, isPending } =
        useMutationWithHandler(
            {
                mutationFn: createSurveyRecordsBulk,
            },
            {
                reactQueryKeys: [
                    QUERY_KEYS.SURVEYS,
                    QUERY_KEYS.SURVEY_ANALYTICS_DASHBOARD,
                ],
            }
        )

    const validateForm = () => {
        if (
            !customerSelection?.selectedCustomers ||
            customerSelection.selectedCustomers.length === 0
        ) {
            return false
        }

        // Check if every selected customer has a response
        // AND every score is valid (1-10)
        // AND referral_willingness is selected
        return customerSelection.selectedCustomers.every((customer) => {
            const response = responses.find(
                (r) => r.customer_id === customer.customer_id
            )
            if (!response || !response.records) return false

            const {
                score_service_quality,
                score_customer_care,
                score_tech_support,
                score_issue_resolution,
                score_referral_willingness,
            } = response.records

            const isValidScore = (score?: number) =>
                typeof score === 'number' &&
                score >= SURVEY_SCORE_MIN &&
                score <= SURVEY_SCORE_MAX

            return (
                isValidScore(score_service_quality) &&
                isValidScore(score_customer_care) &&
                isValidScore(score_tech_support) &&
                isValidScore(score_issue_resolution) &&
                isValidScore(score_referral_willingness)
            )
        })
    }

    const handleSubmit = async () => {
        // Prevent actual submission in tour mode
        if (isTourMode) {
            toast.success(
                t(
                    'surveys.create.demo_mode',
                    'Demo mode - không thực hiện gửi thực sự'
                )
            )
            return
        }

        if (!validateForm()) {
            toast.error(
                t(
                    'surveys.create.complete_all_responses',
                    'Please complete responses for all customers'
                )
            )
            return
        }

        // Stamp the customer's current service groups onto each record: the
        // quarterly report filters on it and it is not recoverable afterwards.
        const payload = responses.map((response) => {
            const customer = customerSelection?.selectedCustomers?.find(
                (c) => c.customer_id === response.customer_id
            )
            const serviceGroups = customer?.service_groups ?? []
            return serviceGroups.length > 0
                ? { ...response, service_name: serviceGroups.join(', ') }
                : response
        })

        // Bulk create all records in single transaction
        const result = await createBulkMutation(payload)

        if (result) {
            toast.success(t('surveys.create.success'))
            sessionStorage.removeItem(SESSION_STORAGE_KEY)
            navigate(generatePathDatacentral(paths.surveys, {}))
        } else {
            toast.error(t('surveys.create.error'))
        }
    }

    useEffect(() => {
        return () => {
            sessionStorage.removeItem(SESSION_STORAGE_KEY)
        }
    }, [])

    useEffect(() => {
        if (customerSelection) {
            sessionStorage.setItem(
                SESSION_STORAGE_KEY,
                JSON.stringify(customerSelection)
            )
        }
    }, [customerSelection])

    const steps = [
        {
            label: t('surveys.create.step1_title', 'Step 1: Choose customer'),
            icon: <User className="w-4 h-4" />,
        },
        {
            label: t('surveys.create.step2_title', 'Step 2: Responses'),
            icon: <FileText className="w-4 h-4" />,
        },
    ]

    const handleNext = () => {
        if (currentStep === 1) {
            // Validate step 1
            if (
                !customerSelection ||
                (customerSelection.mode === 'manual' &&
                    (!customerSelection.selectedIds ||
                        customerSelection.selectedIds.length === 0))
            ) {
                return
            }
            setIsStep2Confirmed(true)
            // Keep only the customers still selected, then seed anyone already
            // surveyed this quarter with what is on file so the form shows their
            // previous answers rather than a blank slate.
            setResponses((prev) => {
                const kept = prev.filter((r) =>
                    customerSelection?.selectedCustomers?.some(
                        (c) => c.customer_id === r.customer_id
                    )
                )
                const seeded = (customerSelection?.selectedCustomers ?? [])
                    .filter(
                        (c) =>
                            !kept.some(
                                (r) => r.customer_id === c.customer_id
                            ) && existingResponsesByCustomer.has(c.customer_id)
                    )
                    .map((c) => ({
                        customer_id: c.customer_id,
                        records: {
                            ...existingResponsesByCustomer.get(c.customer_id),
                        },
                    }))
                return [...kept, ...seeded]
            })
            setCurrentStep(2)
        } else if (currentStep === 2) {
            handleSubmit()
        }
    }

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
            setIsStep2Confirmed(false)
        }
    }

    return (
        <div className="space-y-2" data-tour="survey-create-form">
            <SubHeaders
                breadcrumbs={[
                    { title: t('surveys.title'), path: paths.surveys },
                    {
                        title: t(
                            'surveys.path.records.create',
                            'Create Survey Record'
                        ),
                        path: generatePathDatacentral(
                            paths.surveyRecordCreate,
                            {}
                        ),
                    },
                ]}
            />

            {isTourMode && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-2">
                    <span className="text-purple-600 text-sm font-medium">
                        {t('surveys.create.demo_tour_notice')}
                    </span>
                </div>
            )}

            <div className="bg-white rounded-lg mt-4 shadow-sm">
                <div data-tour="survey-step-indicator">
                    <StepIndicator steps={steps} currentStep={currentStep} />
                </div>

                <div className="border-t">
                    {currentStep === 1 &&
                        (renderQuarterIdsState() ?? (
                            <div data-tour="survey-customer-selection">
                                <CustomerPreviewSection
                                    onSelectionChanged={setCustomerSelection}
                                    defaultMode="manual"
                                    defaultSelection={customerSelection}
                                    requiredCustomerIds={quarterCustomerIds}
                                />
                            </div>
                        ))}

                    {currentStep === 2 &&
                        isStep2Confirmed &&
                        customerSelection && (
                            <div data-tour="survey-response-table">
                                <SurveyResponseTable
                                    customers={
                                        customerSelection.selectedCustomers ||
                                        []
                                    }
                                    responses={responses}
                                    onResponsesChanged={(payload) => {
                                        setResponses((prev) => {
                                            const index = prev.findIndex(
                                                (r) =>
                                                    r.customer_id ===
                                                    payload.customer_id
                                            )
                                            if (index !== -1) {
                                                const next = [...prev]
                                                next[index] = payload
                                                return next
                                            }
                                            return [...prev, payload]
                                        })
                                    }}
                                    onRemoveCustomer={(customerId) => {
                                        // Remove from responses
                                        setResponses((prev) =>
                                            prev.filter(
                                                (r) =>
                                                    r.customer_id !== customerId
                                            )
                                        )

                                        // Remove from customer selection
                                        setCustomerSelection((prev) => {
                                            if (!prev) return prev
                                            return {
                                                ...prev,
                                                selectedIds:
                                                    prev.selectedIds?.filter(
                                                        (id) =>
                                                            id !== customerId
                                                    ) || [],
                                                selectedCustomers:
                                                    prev.selectedCustomers?.filter(
                                                        (c) =>
                                                            c.customer_id !==
                                                            customerId
                                                    ) || [],
                                            }
                                        })
                                    }}
                                />
                            </div>
                        )}
                </div>

                <div className="p-4 border-t flex items-center justify-end gap-4">
                    {currentStep !== 1 && (
                        <Button
                            data-tour="survey-back-button"
                            className="min-w-[120px]"
                            variant="outline"
                            disabled={currentStep === 1 || isPending}
                            onClick={handleBack}
                        >
                            {t('back', 'Back')}
                        </Button>
                    )}
                    <Button
                        data-tour={
                            currentStep === 2
                                ? 'survey-submit-button'
                                : 'survey-next-button'
                        }
                        className="min-w-[120px]"
                        disabled={
                            isPending ||
                            (currentStep === 1 &&
                                // Never leave step 1 before the existing answers
                                // have loaded. A selection restored from
                                // sessionStorage makes this button clickable
                                // immediately, and stepping through early would
                                // render step 2 blank -- submitting that blanks
                                // every note already on file, because the backend
                                // update is a full replace.
                                (quarterSurveyState === undefined ||
                                    !customerSelection ||
                                    (customerSelection.mode === 'manual' &&
                                        (!customerSelection.selectedIds ||
                                            customerSelection.selectedIds
                                                .length === 0))))
                        }
                        type="submit"
                        onClick={handleNext}
                    >
                        {isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {currentStep === 2
                            ? t('surveys.create.submit', 'Submit')
                            : t('next', 'Next')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default SurveyRecordCreate
